import type { WorkType } from '../common/models';
import type { DayOff, DayOffState, DayOffType } from '../settings/models';
import type { PendingEntry } from '../worklog/models';

export type { WorkType };

export type Holiday = {
    date: string;
    name: string;
};

export type ExportRow = {
    rowDate: string;
    hours: number;
    developer: string;
    department: string;
    issueId: number;
    issueTitle: string;
    workItemType: string;
    workTypeLabel: WorkType | null;
    // Who wrote the entry; only the author can change it.
    authorEmail: string;
};

export type NonWorking = { kind: 'weekend' } | { kind: 'holiday'; name: string } | { kind: 'dayOff'; type: DayOffType; state: DayOffState };

export const dayOffLabel = (type: DayOffType): string => {
    switch (type) {
        case 'Vacation':
            return 'Vacation';
        case 'Sickday':
            return 'Sick day';
        default:
            return 'Day off';
    }
};

export const toNonWorkingDayOff = (d: DayOff): NonWorking => ({ kind: 'dayOff', type: d.type, state: d.state });

export type DayCell = {
    date: string;
    inMonth: boolean;
    capacity: number;
    nonWorking: NonWorking | null;
    savedEntries: ExportRow[];
    savedHours: number;
};

export type YearMonth = { year: number; month: number };

export const today = (): YearMonth => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

export const prev = (ym: YearMonth): YearMonth =>
    ym.month === 1 ? { year: ym.year - 1, month: 12 } : { year: ym.year, month: ym.month - 1 };

export const next = (ym: YearMonth): YearMonth =>
    ym.month === 12 ? { year: ym.year + 1, month: 1 } : { year: ym.year, month: ym.month + 1 };

export const label = (ym: YearMonth): { month: string; year: string } => {
    const d = new Date(ym.year, ym.month - 1, 1);
    return {
        month: d.toLocaleDateString('en-US', { month: 'long' }),
        year: String(ym.year),
    };
};

const MONTH_NAMES = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
];

export const toSlug = (ym: YearMonth): string => `${ym.year}-${MONTH_NAMES[ym.month - 1]}`;

export const fromSlug = (s: string): YearMonth | null => {
    const m = s.match(/^(\d{4})-([a-z]+)$/i);
    if (!m) {
        return null;
    }
    const year = Number(m[1]);
    const monthIdx = MONTH_NAMES.indexOf(m[2].toLowerCase());
    if (monthIdx < 0) {
        return null;
    }
    return { year, month: monthIdx + 1 };
};

export type DayDeltas = {
    pendingAdds: PendingEntry[];
    pendingAddHours: number;
    pendingRemoveHours: number;
    pendingNetHours: number;
    removalByKey: Set<string>;
    effectiveSavedHours: number;
    totalHours: number;
};

const removalKey = (entry: PendingEntry): string => `${entry.workItemId}|${entry.hours}`;

export const computeDayDeltas = (cell: DayCell, pendingForDay: readonly PendingEntry[]): DayDeltas => {
    const pendingAdds: PendingEntry[] = [];
    const removalByKey = new Set<string>();
    let pendingAddHours = 0;
    let pendingRemoveHours = 0;

    for (const entry of pendingForDay) {
        if (entry.action === 'remove') {
            removalByKey.add(removalKey(entry));
            pendingRemoveHours += entry.hours;
        } else {
            pendingAdds.push(entry);
            pendingAddHours += entry.hours;
        }
    }

    const effectiveSavedHours = Math.max(0, cell.savedHours - pendingRemoveHours);
    return {
        pendingAdds,
        pendingAddHours,
        pendingRemoveHours,
        pendingNetHours: pendingAddHours - pendingRemoveHours,
        removalByKey,
        effectiveSavedHours,
        totalHours: effectiveSavedHours + pendingAddHours,
    };
};

export const pendingNetHours = (entries: readonly PendingEntry[]): number =>
    entries.reduce((sum, e) => sum + (e.action === 'remove' ? -e.hours : e.hours), 0);

export type AllocationFractions = {
    savedFrac: number;
    pendingFrac: number;
    remainingFrac: number;
};

export const computeAllocationFractions = (capacity: number, saved: number, pending: number, missing?: number): AllocationFractions => {
    if (capacity <= 0) {
        return { savedFrac: 0, pendingFrac: 0, remainingFrac: 0 };
    }
    const effectiveSaved = Math.max(0, saved + Math.min(0, pending));
    const pendingAddOnly = Math.max(0, pending);
    const savedFrac = Math.min(1, effectiveSaved / capacity);
    const pendingFrac = Math.max(0, Math.min(1 - savedFrac, pendingAddOnly / capacity));
    const missingHours = missing ?? Math.max(0, capacity - effectiveSaved - pendingAddOnly);
    const remainingFrac = Math.max(0, Math.min(1 - savedFrac - pendingFrac, missingHours / capacity));
    return { savedFrac, pendingFrac, remainingFrac };
};

export const percentOfCapacity = (allocated: number, capacity: number): number =>
    capacity > 0 ? Math.round((allocated / capacity) * 100) : 0;
